import { useEffect, useRef } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Wordmark } from '@/components/Wordmark';
import { MegaNav } from './MegaNav';
import { MarketingSeo, SearchAnswers } from './MarketingSeo';
import '@/styles/marketing.css';
import '@/styles/brand-refinement.css';
import '@/styles/navigation.css';

export const PRODUCT_PAGES = [
  { to: '/features/cards', label: 'Cards', blurb: 'Frames, exchange, wallet, QR' },
  { to: '/features/shop', label: 'Shop', blurb: 'Products, payments, orders' },
  { to: '/features/leads', label: 'Leads', blurb: 'Pipeline, hot leads, AI replies' },
  { to: '/features/grow', label: 'Grow', blurb: 'Automations, events, integrations' },
];

/**
 * Shell for every marketing page: sticky translucent nav with a Product
 * menu, pointer-glow tracking for dark sections, and the footer. Scroll
 * resets on route change.
 *
 * No smooth-scroll library here on purpose: globals.css gives html/body
 * `overflow-x: hidden` + `height: 100%`, which makes <body> the scroll
 * container, so a wheel hijacker that scrolls `window` freezes the page.
 */
export function MarketingLayout() {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { pathname, hash } = useLocation();

  // Honor cross-page section links once the lazy route has mounted.
  useEffect(() => {
    if (hash) {
      let frame = 0;
      let attempts = 0;
      const seek = () => {
        const target = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (target) target.scrollIntoView({ block: 'start' });
        else if (++attempts < 120) frame = requestAnimationFrame(seek);
      };
      frame = requestAnimationFrame(seek);
      return () => cancelAnimationFrame(frame);
    }
    window.scrollTo({ top: 0 });
    document.body.scrollTop = 0;
  }, [pathname, hash]);

  // Pointer glow for .mk-dark sections — eased in rAF.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce), (pointer: coarse)').matches) return;
    let raf = 0;
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        rootRef.current?.style.setProperty('--mx', `${e.clientX}px`);
        rootRef.current?.style.setProperty('--my', `${e.clientY}px`);
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(raf);
    };
  }, []);


  return (
    <div ref={rootRef} className="mk-root min-h-screen">
      <MarketingSeo />
      <MegaNav />
      <main id="main-content" tabIndex={-1}>
        <Outlet />
        <SearchAnswers />
      </main>
      <Footer />
    </div>
  );
}


function Footer() {
  return (
    <footer className="border-t border-bone-deep bg-bone">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:px-8 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <Wordmark className="text-2xl" />
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-ink-muted">
            A haphe is a joint. The place where two parts hold. Your card is the haphe between a
            meeting and what comes after.
          </p>
          <p className="mk-kicker mt-6 text-ink-muted/70">ἁφή · built to connect</p>
        </div>
        <FooterCol
          title="Product"
          links={[
            ...PRODUCT_PAGES.map((p) => ({ to: p.to, label: p.label })),
            { to: '/pricing', label: 'Pricing' },
          ]}
        />
        <FooterCol
          title="Account"
          links={[
            { to: '/login', label: 'Log in' },
            { to: '/login', label: 'Get your card' },
            { to: '/app', label: 'Dashboard' },
          ]}
        />
        <FooterCol
          title="Legal"
          links={[
            { to: '/terms', label: 'Terms' },
            { to: '/privacy', label: 'Privacy' },
          ]}
        />
      </div>
      <div className="border-t border-bone-deep">
        <p className="mx-auto max-w-6xl px-5 py-5 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted sm:px-8">
          © {new Date().getFullYear()} Haphe · gethaphe.com
        </p>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: { to: string; label: string }[] }) {
  return (
    <div>
      <p className="mk-kicker text-ink-muted">{title}</p>
      <ul className="mt-3 space-y-2">
        {links.map((l) => (
          <li key={l.label}>
            <Link to={l.to} className="text-sm text-ink-soft transition-colors hover:text-ink">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
